import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Link, Route, Routes, useNavigate } from "react-router-dom";
import type { AccountServices } from "../core/account/types";
import { LoadingScreen, MessageScreen } from "../components/StatusScreen";
import { PrivacyScreen } from "../features/account/PrivacyScreen";
import { ResetPasswordScreen } from "../features/account/ResetPasswordScreen";
import { SignInScreen } from "../features/account/SignInScreen";
import { WelcomeScreen } from "../features/account/WelcomeScreen";
import { en } from "../i18n/en";
import { webServices } from "../platform/web/supabase";
import { AccountProvider, useAccount } from "./account";
import { createQueryClient } from "./queryClient";
import { RequireAccount } from "./RequireAccount";

// Scan (and later OCR) is heavy, so it's code-split (specs/09 FR-39). Like everything behind
// sign-in, these chunks only load for signed-in people (specs/12 FR-89).
const DashboardScreen = lazy(() => import("../features/dashboard/DashboardScreen"));
const ScanScreen = lazy(() => import("../features/scan/ScanScreen"));
const SearchScreen = lazy(() => import("../features/search/SearchScreen"));
const CardDetailScreen = lazy(() => import("../features/cards/CardDetailScreen"));
const PortfolioScreen = lazy(() => import("../features/portfolio/PortfolioScreen"));
const SettingsScreen = lazy(() => import("../features/settings/SettingsScreen"));

const queryClient = createQueryClient();

function NotFound() {
  return (
    <div className="p-6 text-center">
      <h1 className="text-xl font-semibold">{en.notFound.title}</h1>
      <Link to="/scan" className="mt-4 inline-block text-sky-400 underline">
        {en.notFound.home}
      </Link>
    </div>
  );
}

/** Invite and reset links land on the site's root; this opens the matching screen (11 FR-57). */
function LinkRedirects() {
  const { linkEvent } = useAccount();
  const navigate = useNavigate();
  useEffect(() => {
    if (linkEvent === "invite") navigate("/welcome", { replace: true });
    if (linkEvent === "password-reset") navigate("/reset-password", { replace: true });
  }, [linkEvent, navigate]);
  return null;
}

export function AppRoutes() {
  return (
    <>
      <LinkRedirects />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public (specs/12 FR-79). */}
          <Route path="sign-in" element={<SignInScreen />} />
          <Route path="welcome" element={<WelcomeScreen />} />
          <Route path="reset-password" element={<ResetPasswordScreen />} />
          <Route path="privacy" element={<PrivacyScreen />} />
          {/* Everything else needs an account, including not found. */}
          <Route element={<RequireAccount />}>
            <Route index element={<DashboardScreen />} />
            <Route path="scan" element={<ScanScreen />} />
            <Route path="search" element={<SearchScreen />} />
            <Route path="portfolio" element={<PortfolioScreen />} />
            <Route path="card/:lang/:id" element={<CardDetailScreen />} />
            <Route path="settings" element={<SettingsScreen />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

export function App({ services = webServices }: { services?: AccountServices | null }) {
  // No guest fallback: without a sign-in service there's no app (specs/12 edge cases).
  if (!services) {
    return (
      <MessageScreen title={en.account.notConfigured.title} body={en.account.notConfigured.body} />
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <AccountProvider services={services}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AccountProvider>
    </QueryClientProvider>
  );
}
